import { Calendar, Clock, Users, AlertCircle, Move, Eye, MapPin, CheckCircle } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  BenefitCard,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const features = [
  {
    icon: Calendar,
    title: "Vue calendrier complète",
    description: "Visualisez les affectations par semaine ou par mois en un coup d'œil.",
    gradient: "from-[#06B6D4] to-[#0891B2]",
  },
  {
    icon: Clock,
    title: "Demi-journées",
    description: "Gérez les affectations matin et après-midi séparément.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: Move,
    title: "Glisser-déposer",
    description: "Modifiez les affectations simplement par drag & drop.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
  {
    icon: AlertCircle,
    title: "Détection des conflits",
    description: "Alertes automatiques si un membre est affecté à 2 chantiers en même temps.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
];

const benefits = [
  {
    icon: Eye,
    title: "Vision claire de la charge",
    description: "Voyez qui travaille où et identifiez les surcharges ou sous-charges.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: CheckCircle,
    title: "Moins d'erreurs",
    description: "Fini les doubles affectations et les oublis de planning.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: MapPin,
    title: "Optimisation des déplacements",
    description: "Regroupez les membres par zone géographique.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: Users,
    title: "Traçabilité pour le client",
    description: "Historique complet des présences sur chaque chantier.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
];

const PlanningFeature = () => {
  usePageMeta({ title: "Planning chantier BTP — Gestion équipes | B8ild", description: "Planifiez vos équipes et ressources sur vos chantiers. Visualisez la charge de travail et évitez les conflits de planning.", url: "https://b8ild.fr/product/planning" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Planning chantier BTP — Gestion équipes | B8ild",
        description: "Planifiez vos équipes et ressources sur vos chantiers. Visualisez la charge de travail et évitez les conflits de planning.",
        url: "https://b8ild.fr/product/planning",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Planning", item: "https://b8ild.fr/product/planning" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Planning & Rotations" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Planning & Rotations"
        title="Qui travaille où,"
        titleHighlight="et quand ?"
        subtitle="Gérez les affectations de vos équipes sur tous vos chantiers. Demi-journées, rotations, remplacements — tout est tracé."
        icon={Calendar}
        gradient="from-[#06B6D4] to-[#0891B2]"
      />

      {/* Features */}
      <FeatureSection
        title="Fonctionnalités du planning"
        subtitle="Un planning pensé pour le BTP"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <BenefitCard key={index} {...feature} />
          ))}
        </div>
      </FeatureSection>

      {/* Calendar Preview */}
      <FeatureSection variant="dark" title="Vue calendrier intuitive">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-white">Janvier 2026</h3>
                  <div className="flex gap-1">
                    <button className="px-3 py-1 text-xs text-white/50 hover:text-white hover:bg-white/[0.05] rounded-md transition-colors">
                      Semaine
                    </button>
                    <button className="px-3 py-1 text-xs bg-[#F59E0B]/10 text-[#F59E0B] rounded-md">
                      Mois
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="p-2 text-white/50 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors">
                    ←
                  </button>
                  <button className="p-2 text-white/50 hover:text-white hover:bg-white/[0.05] rounded-lg transition-colors">
                    →
                  </button>
                </div>
              </div>

              {/* Calendar grid */}
              <div className="p-6">
                {/* Days header */}
                <div className="grid grid-cols-7 gap-2 mb-4">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                    <div key={day} className="text-center text-xs text-white/40 font-medium py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-2">
                  {/* Week 1 */}
                  {[null, null, 1, 2, 3, 4, 5].map((day, i) => (
                    <div
                      key={i}
                      className={`min-h-[80px] rounded-lg p-2 ${
                        day ? 'bg-white/[0.02] border border-white/[0.05]' : ''
                      }`}
                    >
                      {day && (
                        <>
                          <span className="text-xs text-white/50">{day}</span>
                          {day === 2 && (
                            <div className="mt-1 space-y-1">
                              <div className="text-[10px] bg-[#10B981]/20 text-[#10B981] px-1.5 py-0.5 rounded truncate">
                                Villa Durand
                              </div>
                            </div>
                          )}
                          {day === 3 && (
                            <div className="mt-1 space-y-1">
                              <div className="text-[10px] bg-[#3B82F6]/20 text-[#3B82F6] px-1.5 py-0.5 rounded truncate">
                                Réno. Martin
                              </div>
                              <div className="text-[10px] bg-[#F59E0B]/20 text-[#F59E0B] px-1.5 py-0.5 rounded truncate">
                                Ext. Dupont
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}

                  {/* Week 2 */}
                  {[6, 7, 8, 9, 10, 11, 12].map((day, i) => (
                    <div
                      key={i}
                      className="min-h-[80px] rounded-lg p-2 bg-white/[0.02] border border-white/[0.05]"
                    >
                      <span className={`text-xs ${day === 8 ? 'text-[#F59E0B]' : 'text-white/50'}`}>
                        {day}
                      </span>
                      {day === 8 && (
                        <div className="mt-1">
                          <div className="text-[10px] bg-[#10B981]/20 text-[#10B981] px-1.5 py-0.5 rounded truncate">
                            Villa Durand
                          </div>
                        </div>
                      )}
                      {day === 9 && (
                        <div className="mt-1">
                          <div className="text-[10px] bg-[#EF4444]/20 text-[#EF4444] px-1.5 py-0.5 rounded truncate">
                            ⚠️ Conflit
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Benefits */}
      <FeatureSection
        title="Ce que vous y gagnez"
        subtitle="Un planning efficace = des équipes optimisées"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => (
            <BenefitCard key={index} {...benefit} />
          ))}
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Quand le mauvais gars est au mauvais endroit
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Luc est electricien avec 3 ouvriers. Il gere 6 chantiers en meme temps. Lundi, son apprenti devait etre sur le chantier rue Pasteur. Mais Luc l'a envoye en urgence sur un depannage rue Victor Hugo. Resultat : le chantier Pasteur prend 2 jours de retard.
              </p>
              <p>
                Pendant ce temps, les 2 autres ouvriers sont a 3 sur le chantier boulevard Gambetta — un de trop. 180 EUR/jour de main d'oeuvre gaspillee.
              </p>
              <p>
                Sur un mois, ces conflits de planning coutent 1 500 a 3 000 EUR. Invisible si vous gerez au carnet ou par SMS. Avec un planning visuel qui montre qui est ou, quand, et combien ca coute, ces erreurs disparaissent. Le bon ouvrier au bon endroit, au bon moment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment planifier les equipes sur plusieurs chantiers ?",
                  a: "Listez les taches par chantier et par semaine. Identifiez les competences requises (un apprenti ne peut pas faire le meme travail qu'un chef d'equipe). Evitez d'affecter un ouvrier sur plus de 2 chantiers la meme semaine — les deplacements mangent du temps productif. B8ild affecte par demi-journee et calcule le cout automatiquement.",
                },
                {
                  q: "Un outil de planning gratuit suffit-il ?",
                  a: "Google Agenda ou un tableau Excel montre ou sont vos ouvriers. Mais il ne calcule pas le cout. Si vous envoyez un ouvrier a 25 EUR/h sur un chantier dont la marge est deja fragile, il faut le savoir. B8ild lie le planning au financier : chaque affectation a un cout visible en temps reel.",
                },
                {
                  q: "Comment gerer les absences imprevues ?",
                  a: "Identifiez les chantiers critiques (proches du jour critique ou avec des penalites de retard). Reaffectez en priorite sur ceux-la. Un chantier avec 15% de marge peut absorber 1-2 jours de retard. Un chantier a 5% de marge ne le peut pas.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Optimisez vos affectations"
        subtitle="Un planning clair pour des équipes efficaces."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="planning" />

      <LandingFooter />
    </div>
  );
};

export default PlanningFeature;
